import React from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, Flame, Snowflake, Target } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import Layout, { Breadcrumbs } from "../components/Layout";
import Seo from "../components/Seo";
import { getPrediction, getFrequency } from "../lib/api";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Analytics", path: "/analytics/" },
];

const CARD_STYLES = [
  { bg: "linear-gradient(150deg,#16406e,#0d2946)", accent: "#5eb0ff" },
  { bg: "linear-gradient(150deg,#0f9b8a,#0b6e62)", accent: "#7ff0e2" },
  { bg: "linear-gradient(150deg,#b23a2e,#7e2820)", accent: "#ffb3a8" },
  { bg: "linear-gradient(150deg,#5a3a86,#341f52)", accent: "#d3b3ff" },
];

function PredictionCard({ p, i }) {
  const s = CARD_STYLES[i % CARD_STYLES.length];
  return (
    <div
      data-testid={`prediction-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="teer-card fade-in rounded-2xl p-4 text-white shadow-md"
      style={{ background: s.bg, animationDelay: `${i * 60}ms` }}
    >
      <div className="flex items-center gap-2">
        <Target size={16} style={{ color: s.accent }} />
        <h3 className="text-sm font-bold sm:text-base">{p.label}</h3>
      </div>
      <p className="mt-0.5 text-xs opacity-70">{p.hint}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {p.numbers.map((n) => (
          <span
            key={n}
            className="rounded-lg bg-white/15 px-3 py-1.5 text-xl font-extrabold tracking-wide backdrop-blur-sm sm:text-2xl"
          >
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const { data: pred } = useQuery({ queryKey: ["prediction"], queryFn: getPrediction });
  const { data: freq } = useQuery({ queryKey: ["frequency"], queryFn: getFrequency });

  return (
    <Layout>
      <Seo
        title="Bhutan Morning Teer Analytics – Daily Target & Prediction"
        description="Bhutan Morning Teer analytics with daily target prediction based on previous results, plus hot & cold number trends. Informational only."
        path="/analytics/"
        breadcrumb={crumbs}
      />
      <Breadcrumbs items={crumbs} />

      <div className="mx-auto max-w-3xl px-4 py-4">
        <h1 className="text-2xl font-extrabold text-[#10161f] sm:text-3xl">
          Bhutan Morning Teer Analytics
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Automatic daily target prediction based on the previous result. Updates
          automatically every day.
        </p>

        {pred?.based_on && (
          <div
            data-testid="prediction-basis"
            className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm"
          >
            <TrendingUp size={16} className="text-[#0f9b8a]" />
            <span className="font-semibold text-[#10161f]">Today's Prediction</span>
            <span className="text-slate-500">
              (based on {pred.based_on.date} result — F/R{" "}
              <b className="text-[#10161f]">{pred.based_on.fr}</b>, S/R{" "}
              <b className="text-[#10161f]">{pred.based_on.sr}</b>)
            </span>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(pred?.predictions || []).map((p, i) => (
            <PredictionCard key={p.label} p={p} i={i} />
          ))}
        </div>

        {/* Hot & cold */}
        {freq && (
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 p-4">
              <Flame className="text-orange-500" size={28} />
              <div>
                <p className="text-xs font-medium text-slate-500">Hot ending digit</p>
                <p className="text-2xl font-extrabold text-orange-600" data-testid="hot-digit">
                  {freq.hot}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4">
              <Snowflake className="text-sky-500" size={28} />
              <div>
                <p className="text-xs font-medium text-slate-500">Cold ending digit</p>
                <p className="text-2xl font-extrabold text-sky-600" data-testid="cold-digit">
                  {freq.cold}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Frequency chart */}
        {freq && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="mb-1 text-base font-bold text-[#10161f]">
              Ending Digit Frequency
            </h2>
            <p className="mb-3 text-xs text-slate-500">
              How often each ending digit (0–9) appeared over the last{" "}
              {freq.sample_days} days.
            </p>
            <div style={{ width: "100%", height: 240 }} data-testid="frequency-chart">
              <ResponsiveContainer>
                <BarChart data={freq.digits} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="digit" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                    {freq.digits.map((d) => (
                      <Cell
                        key={d.digit}
                        fill={
                          d.digit === freq.hot
                            ? "#f97316"
                            : d.digit === freq.cold
                            ? "#38bdf8"
                            : "#1ab5a3"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <p className="mt-5 text-xs text-slate-500">
          Disclaimer: These predictions are generated automatically from past
          results for informational and entertainment purposes only. They do not
          guarantee any outcome. Please play responsibly.
        </p>
      </div>
    </Layout>
  );
}
